import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { assessmentAPI, dashboardAPI } from '../lib/api';
import {
  Shield,
  Plus,
  FileText,
  Clock,
  CheckCircle,
  BarChart3,
  ChevronRight,
  Loader2,
  LogOut,
  Trash2,
  AlertCircle,
  Crown,
  Sparkles,
  FolderOpen,
  ClipboardList,
  Building2,
  Award,
  ShieldCheck,
  Play,
  ArrowRight,
  Share2,
  Copy,
  MoreVertical,
  X,
  Mail,
  Link,
  Users
} from 'lucide-react';
import VulcanBadge from '../components/VulcanBadge';
import AssessmentProgressWidget from '../components/AssessmentProgressWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [recentHipaaAssessment, setRecentHipaaAssessment] = useState(null);
  const [showShareModal, setShowShareModal] = useState(null); // assessment ID or null
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // assessment ID or null
  const [allUsers, setAllUsers] = useState([]);
  const [shareEmail, setShareEmail] = useState('');
  const [sharePermission, setSharePermission] = useState('view');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [actionMenuOpen, setActionMenuOpen] = useState(null); // assessment ID or null
  const [newAssessment, setNewAssessment] = useState({
    name: 'My Assessment',
    assessmentType: 'HIPAA',  // Default to HIPAA since it has the new questionnaire
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch users for sharing once the user is authenticated
  useEffect(() => {
    if (user?.userId) {
      fetchAllUsers();
    }
  }, [user?.userId]);

  const fetchData = async () => {
    try {
      const [assessmentsRes, summaryRes] = await Promise.all([
        assessmentAPI.getAll(),
        dashboardAPI.getSummary()
      ]);
      setAssessments(assessmentsRes.data);
      setSummary(summaryRes.data);
      
      // Fetch recent HIPAA assessments for quick resume
      try {
        const recentRes = await fetch(`${API_URL}/api/hipaa-assessment/recent/${user?.id || 'default'}?limit=1`);
        const recentData = await recentRes.json();
        if (recentData.recentAssessments?.length > 0) {
          setRecentHipaaAssessment(recentData.recentAssessments[0]);
        }
      } catch (e) {
        console.log('No recent HIPAA assessments');
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const token = localStorage.getItem('cset_token');
      if (!token) {
        return;
      }
      // Use the non-admin endpoint for getting users available for sharing
      const res = await fetch(`${API_URL}/api/users/for-sharing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Filter out current user
        const filteredUsers = data.filter(u => u.id !== user?.userId && u.id !== user?.id);
        setAllUsers(filteredUsers);
      }
    } catch (e) {
      console.log('Could not fetch users for sharing');
    }
  };

  const handleCreateAssessment = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await assessmentAPI.create(newAssessment);
      // Use unified Assessment.js for all assessment types (includes changelog, bulk actions, etc.)
      navigate(`/assessment/${response.data.id}`);
    } catch (error) {
      console.error('Failed to create assessment:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteAssessment = async (id) => {
    try {
      await assessmentAPI.delete(id);
      setAssessments(assessments.filter(a => a.id !== id));
      setShowDeleteConfirm(null);
      fetchData(); // Refresh summary
    } catch (error) {
      console.error('Failed to delete assessment:', error);
    }
  };

  const handleDuplicateAssessment = async (assessment, e) => {
    e.stopPropagation();
    setActionMenuOpen(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/assessment/${assessment.id}/duplicate`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (res.ok) {
        fetchData(); // Refresh list
        alert('Assessment duplicated successfully!');
      } else {
        alert('Failed to duplicate assessment');
      }
    } catch (error) {
      console.error('Failed to duplicate assessment:', error);
      alert('Failed to duplicate assessment');
    }
  };

  const handleShareAssessment = async (assessmentId) => {
    try {
      const token = localStorage.getItem('cset_token');
      const shareData = {
        userIds: selectedUsers,
        email: shareEmail || null,
        permission: sharePermission
      };
      
      const res = await fetch(`${API_URL}/api/assessment/${assessmentId}/share-with-users`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(shareData)
      });
      
      if (res.ok) {
        setShowShareModal(null);
        setSelectedUsers([]);
        setShareEmail('');
        setSharePermission('view');
        alert('Assessment shared successfully!');
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to share assessment');
      }
    } catch (error) {
      console.error('Failed to share assessment:', error);
      alert('Failed to share assessment');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-violet-500 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard" className="min-h-screen bg-gradient-to-br from-slate-900 via-violet-950/30 to-slate-900">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-slate-800/50 rounded-xl p-6 border border-violet-500/20">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-violet-500/20 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-violet-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Total Assessments</p>
                <p className="text-2xl font-bold text-white">{summary?.totalAssessments || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Completed</p>
                <p className="text-2xl font-bold text-white">{summary?.completed || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">In Progress</p>
                <p className="text-2xl font-bold text-white">{summary?.inProgress || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Avg. Score</p>
                <p className="text-2xl font-bold text-white">{summary?.averageScore || 0}%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Resume HIPAA Assessment Card */}
        {recentHipaaAssessment && recentHipaaAssessment.progress?.percentage < 100 && (
          <div className="mb-8">
            <div 
              onClick={() => navigate(recentHipaaAssessment.resumeUrl)}
              className="bg-gradient-to-r from-violet-900/50 to-fuchsia-900/30 border border-violet-500/50 rounded-xl p-6 cursor-pointer hover:border-violet-400 transition-all group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-violet-500/20 rounded-xl">
                    <Play className="w-8 h-8 text-violet-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl font-bold text-white">Resume HIPAA Assessment</h3>
                      <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs font-semibold rounded-full">
                        {recentHipaaAssessment.progress.percentage}% complete
                      </span>
                    </div>
                    <p className="text-slate-400">
                      {recentHipaaAssessment.progress.answered} of {recentHipaaAssessment.progress.total} questions answered
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-32 h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded-full transition-all"
                      style={{ width: `${recentHipaaAssessment.progress.percentage}%` }}
                    />
                  </div>
                  <ArrowRight className="w-6 h-6 text-violet-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Assessment Progress Widget */}
        <AssessmentProgressWidget assessments={assessments} />

        {/* Action Button */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">Your Assessments</h2>
          <button
            data-testid="create-assessment-btn"
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-colors shadow-lg shadow-violet-500/25"
          >
            <Plus className="w-5 h-5" />
            New Assessment
          </button>
        </div>

        {/* Assessments List */}
        {assessments.length === 0 ? (
          <div className="bg-slate-800/50 rounded-xl p-12 border border-violet-500/20 text-center">
            <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-slate-500" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">No Assessments Yet</h3>
            <p className="text-slate-400 mb-6">Create your first cybersecurity assessment to get started.</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
            >
              <Plus className="w-5 h-5" />
              Create Assessment
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {assessments.map((assessment) => {
              const typeConfig = {
                'CMMC': { bg: 'bg-blue-500/20', text: 'text-blue-400' },
                'HIPAA': { bg: 'bg-green-500/20', text: 'text-green-400' },
                'NIST_CSF': { bg: 'bg-purple-500/20', text: 'text-purple-400' },
                'FINRA': { bg: 'bg-amber-500/20', text: 'text-amber-400' },
                'SOC2': { bg: 'bg-cyan-500/20', text: 'text-cyan-400' },
                'SOC2_TYPE2': { bg: 'bg-teal-500/20', text: 'text-teal-400' }
              };
              const config = typeConfig[assessment.assessmentType] || typeConfig['HIPAA'];
              const displayType = assessment.assessmentType === 'NIST_CSF' ? 'NIST CSF' : 
                                 assessment.assessmentType === 'SOC2' ? 'SOC 2 Type 1' :
                                 assessment.assessmentType === 'SOC2_TYPE2' ? 'SOC 2 Type 2' : assessment.assessmentType;
              
              return (
              <div
                key={assessment.id}
                data-testid={`assessment-card-${assessment.id}`}
                onClick={() => {
                  // Use unified Assessment.js for all assessment types (includes changelog, bulk actions, etc.)
                  navigate(`/assessment/${assessment.id}`);
                }}
                className={`bg-slate-800/50 rounded-xl p-6 border ${
                  assessment.score === 100 
                    ? 'border-violet-500/50 bg-gradient-to-r from-violet-900/20 to-slate-800/50' 
                    : 'border-slate-700 hover:border-blue-500/50'
                } cursor-pointer transition-all group`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      assessment.score === 100 ? 'bg-violet-500/20' : config.bg
                    }`}>
                      {assessment.score === 100 ? (
                        <span className="text-2xl">🖖</span>
                      ) : (
                        <Shield className={`w-6 h-6 ${config.text}`} />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-white group-hover:text-blue-400 transition-colors">
                          {assessment.name}
                        </h3>
                        {assessment.isShared && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-sky-500/30 text-sky-300 text-xs rounded-full">
                            <Users className="w-3 h-3" />
                            Shared
                          </span>
                        )}
                        {assessment.score === 100 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-violet-500/30 text-violet-300 text-xs rounded-full">
                            <Award className="w-3 h-3" />
                            Vulcan Level
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1">
                        <span className={`text-xs px-2 py-1 rounded-full ${config.bg} ${config.text}`}>
                          {displayType}
                        </span>
                        <span className="text-slate-400 text-sm">
                          {formatDate(assessment.createdAt)}
                        </span>
                        <span className={`text-sm ${
                          assessment.status === 'Completed' 
                            ? 'text-green-400' 
                            : 'text-yellow-400'
                        }`}>
                          {assessment.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <div className={`text-2xl font-bold ${
                        assessment.score === 100 ? 'text-violet-400' : 'text-white'
                      }`}>{assessment.score}%</div>
                      <div className="text-sm text-slate-400">
                        {assessment.answeredQuestions}/{assessment.totalQuestions} answered
                      </div>
                    </div>
                    {/* Generate SSP Report button for HIPAA assessments */}
                    {assessment.assessmentType === 'HIPAA' && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const res = await fetch(`${API_URL}/api/ssp-reports`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                assessmentId: assessment.id,
                                assessmentName: assessment.name
                              })
                            });
                            if (res.ok) {
                              // Navigate directly to SSP Report page with assessment data
                              navigate(`/ssp-report?assessmentId=${assessment.id}`);
                            }
                          } catch (err) {
                            console.error('Error generating SSP report:', err);
                          }
                        }}
                        className={`flex items-center gap-2 px-3 py-2 ${
                          assessment.score >= 100 
                            ? 'bg-emerald-600 hover:bg-emerald-500' 
                            : 'bg-violet-600 hover:bg-violet-500'
                        } text-white text-sm rounded-lg transition-colors`}
                        data-testid={`generate-ssp-${assessment.id}`}
                      >
                        <FileText className="w-4 h-4" />
                        {assessment.score >= 100 ? 'SSP Report' : 'View SSP'}
                      </button>
                    )}
                    
                    {/* Actions Menu */}
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActionMenuOpen(actionMenuOpen === assessment.id ? null : assessment.id);
                        }}
                        className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                        data-testid={`action-menu-${assessment.id}`}
                      >
                        <MoreVertical className="w-5 h-5" />
                      </button>
                      
                      {actionMenuOpen === assessment.id && (
                        <div className="absolute right-0 top-10 w-48 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-20 py-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActionMenuOpen(null);
                              setShowShareModal(assessment.id);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2 text-left text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
                          >
                            <Share2 className="w-4 h-4" />
                            Share
                          </button>
                          <button
                            onClick={(e) => handleDuplicateAssessment(assessment, e)}
                            className="w-full flex items-center gap-3 px-4 py-2 text-left text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
                          >
                            <Copy className="w-4 h-4" />
                            Duplicate
                          </button>
                          <hr className="my-1 border-slate-700" />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActionMenuOpen(null);
                              setShowDeleteConfirm(assessment.id);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2 text-left text-red-400 hover:bg-red-500/10 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-blue-400 transition-colors" />
                  </div>
                </div>
                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${
                        assessment.score === 100 ? 'bg-violet-500' :
                        assessment.status === 'Completed' ? 'bg-green-500' : 'bg-blue-500'
                      }`}
                      style={{ width: `${(assessment.answeredQuestions / assessment.totalQuestions) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            )})}
          </div>
        )}
      </main>

      {/* Create Assessment Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl p-8 w-full max-w-md border border-slate-700">
            <h2 className="text-2xl font-bold text-white mb-6">Create New Assessment</h2>
            
            <form onSubmit={handleCreateAssessment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Assessment Name
                </label>
                <input
                  data-testid="new-assessment-name"
                  type="text"
                  value={newAssessment.name}
                  onChange={(e) => setNewAssessment({ ...newAssessment, name: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="My Assessment"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Assessment Type
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    data-testid="select-hipaa"
                    onClick={() => setNewAssessment({ ...newAssessment, assessmentType: 'HIPAA' })}
                    className={`p-3 rounded-lg border-2 transition-all relative overflow-visible ${
                      newAssessment.assessmentType === 'HIPAA'
                        ? 'border-green-500 bg-green-500/20'
                        : 'border-slate-600 hover:border-slate-500'
                    }`}
                  >
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 text-[10px] font-bold bg-violet-500 text-white rounded-full z-10">NEW</span>
                    <AlertCircle className={`w-6 h-6 mx-auto mb-1 ${
                      newAssessment.assessmentType === 'HIPAA' ? 'text-green-400' : 'text-slate-400'
                    }`} />
                    <div className={`font-semibold text-sm ${
                      newAssessment.assessmentType === 'HIPAA' ? 'text-green-400' : 'text-slate-300'
                    }`}>HIPAA</div>
                    <div className="text-xs text-slate-500">Healthcare (158)</div>
                  </button>
                  <button
                    type="button"
                    data-testid="select-cmmc"
                    onClick={() => setNewAssessment({ ...newAssessment, assessmentType: 'CMMC' })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      newAssessment.assessmentType === 'CMMC'
                        ? 'border-blue-500 bg-blue-500/20'
                        : 'border-slate-600 hover:border-slate-500'
                    }`}
                  >
                    <Shield className={`w-6 h-6 mx-auto mb-1 ${
                      newAssessment.assessmentType === 'CMMC' ? 'text-blue-400' : 'text-slate-400'
                    }`} />
                    <div className={`font-semibold text-sm ${
                      newAssessment.assessmentType === 'CMMC' ? 'text-blue-400' : 'text-slate-300'
                    }`}>CMMC</div>
                    <div className="text-xs text-slate-500">Maturity (125)</div>
                  </button>
                  <button
                    type="button"
                    data-testid="select-nist-csf"
                    onClick={() => setNewAssessment({ ...newAssessment, assessmentType: 'NIST_CSF' })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      newAssessment.assessmentType === 'NIST_CSF'
                        ? 'border-purple-500 bg-purple-500/20'
                        : 'border-slate-600 hover:border-slate-500'
                    }`}
                  >
                    <Sparkles className={`w-6 h-6 mx-auto mb-1 ${
                      newAssessment.assessmentType === 'NIST_CSF' ? 'text-purple-400' : 'text-slate-400'
                    }`} />
                    <div className={`font-semibold text-sm ${
                      newAssessment.assessmentType === 'NIST_CSF' ? 'text-purple-400' : 'text-slate-300'
                    }`}>NIST CSF</div>
                    <div className="text-xs text-slate-500">Framework (106)</div>
                  </button>
                  <button
                    type="button"
                    data-testid="select-finra"
                    onClick={() => setNewAssessment({ ...newAssessment, assessmentType: 'FINRA' })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      newAssessment.assessmentType === 'FINRA'
                        ? 'border-amber-500 bg-amber-500/20'
                        : 'border-slate-600 hover:border-slate-500'
                    }`}
                  >
                    <Building2 className={`w-6 h-6 mx-auto mb-1 ${
                      newAssessment.assessmentType === 'FINRA' ? 'text-amber-400' : 'text-slate-400'
                    }`} />
                    <div className={`font-semibold text-sm ${
                      newAssessment.assessmentType === 'FINRA' ? 'text-amber-400' : 'text-slate-300'
                    }`}>FINRA</div>
                    <div className="text-xs text-slate-500">Financial (85)</div>
                  </button>
                  <button
                    type="button"
                    data-testid="select-soc2"
                    onClick={() => setNewAssessment({ ...newAssessment, assessmentType: 'SOC2' })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      newAssessment.assessmentType === 'SOC2'
                        ? 'border-cyan-500 bg-cyan-500/20'
                        : 'border-slate-600 hover:border-slate-500'
                    }`}
                  >
                    <ShieldCheck className={`w-6 h-6 mx-auto mb-1 ${
                      newAssessment.assessmentType === 'SOC2' ? 'text-cyan-400' : 'text-slate-400'
                    }`} />
                    <div className={`font-semibold text-sm ${
                      newAssessment.assessmentType === 'SOC2' ? 'text-cyan-400' : 'text-slate-300'
                    }`}>SOC 2 Type 1</div>
                    <div className="text-xs text-slate-500">Point-in-Time (110)</div>
                  </button>
                  <button
                    type="button"
                    data-testid="select-soc2-type2"
                    onClick={() => setNewAssessment({ ...newAssessment, assessmentType: 'SOC2_TYPE2' })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      newAssessment.assessmentType === 'SOC2_TYPE2'
                        ? 'border-teal-500 bg-teal-500/20'
                        : 'border-slate-600 hover:border-slate-500'
                    }`}
                  >
                    <Award className={`w-6 h-6 mx-auto mb-1 ${
                      newAssessment.assessmentType === 'SOC2_TYPE2' ? 'text-teal-400' : 'text-slate-400'
                    }`} />
                    <div className={`font-semibold text-sm ${
                      newAssessment.assessmentType === 'SOC2_TYPE2' ? 'text-teal-400' : 'text-slate-300'
                    }`}>SOC 2 Type 2</div>
                    <div className="text-xs text-slate-500">Operating Effectiveness (125)</div>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  data-testid="new-assessment-description"
                  value={newAssessment.description}
                  onChange={(e) => setNewAssessment({ ...newAssessment, description: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 h-24 resize-none"
                  placeholder="Brief description of this assessment..."
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 py-3 border border-slate-600 text-slate-300 font-semibold rounded-lg hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="create-assessment-submit"
                  disabled={creating || !newAssessment.name}
                  className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Assessment'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Share Assessment Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowShareModal(null)}>
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-lg border border-slate-700" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Share2 className="w-5 h-5 text-violet-400" />
                Share Assessment
              </h2>
              <button onClick={() => setShowShareModal(null)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Share via Email */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                <Mail className="w-4 h-4" /> Invite by Email
              </label>
              <input
                type="email"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                placeholder="Enter email address"
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
            
            {/* Select Users */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                <Users className="w-4 h-4" /> Select Users
              </label>
              <div className="max-h-40 overflow-y-auto bg-slate-700/50 rounded-lg border border-slate-600">
                {allUsers.length === 0 ? (
                  <p className="p-4 text-slate-400 text-sm text-center">No other users available</p>
                ) : (
                  allUsers.map(u => (
                    <label key={u.id} className="flex items-center gap-3 p-3 hover:bg-slate-700 cursor-pointer border-b border-slate-600/50 last:border-0">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(u.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUsers([...selectedUsers, u.id]);
                          } else {
                            setSelectedUsers(selectedUsers.filter(id => id !== u.id));
                          }
                        }}
                        className="w-4 h-4 rounded border-slate-500 text-violet-500 focus:ring-violet-500 bg-slate-600"
                      />
                      <div>
                        <p className="text-white text-sm">{u.firstName} {u.lastName}</p>
                        <p className="text-slate-400 text-xs">{u.email}</p>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
            
            {/* Permission Level */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">Permission Level</label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setSharePermission('view')}
                  className={`flex-1 py-2 px-4 rounded-lg border-2 transition-all ${
                    sharePermission === 'view' 
                      ? 'border-violet-500 bg-violet-500/20 text-violet-300' 
                      : 'border-slate-600 text-slate-400 hover:border-slate-500'
                  }`}
                >
                  View Only
                </button>
                <button
                  type="button"
                  onClick={() => setSharePermission('edit')}
                  className={`flex-1 py-2 px-4 rounded-lg border-2 transition-all ${
                    sharePermission === 'edit' 
                      ? 'border-emerald-500 bg-emerald-500/20 text-emerald-300' 
                      : 'border-slate-600 text-slate-400 hover:border-slate-500'
                  }`}
                >
                  Can Edit
                </button>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowShareModal(null)}
                className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleShareAssessment(showShareModal)}
                disabled={!shareEmail && selectedUsers.length === 0}
                className="flex-1 py-3 bg-violet-600 hover:bg-violet-500 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteConfirm(null)}>
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-md border border-red-500/30" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Delete Assessment</h2>
                <p className="text-slate-400 text-sm">This action cannot be undone.</p>
              </div>
            </div>
            
            <p className="text-slate-300 mb-6">
              Are you sure you want to permanently delete this assessment? All data including responses, comments, and evidence will be lost.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteAssessment(showDeleteConfirm)}
                className="flex-1 py-3 bg-red-600 hover:bg-red-500 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
